/**
 * @fileoverview Input/output schemas for the style-analyzer-v2 agent.
 *
 * This agent analyzes writing samples and influences to determine
 * their stylistic properties on a 0-4 scale across 14 dimensions.
 */

import { z } from 'zod';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { TemplateInfluenceProseSchema } from '@/domains/style-guides/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Flow Input Schema - accepts loaded prose objects for the agent.
 * Includes execution fields (logId, model, customPrompt).
 *
 * Note: The hook converts template IDs to template prose objects before
 * calling executeAgent. This schema expects the loaded templates.
 */
export const InputSchema = z.object({
  logId: z.string(),
  model: z.string(),
  customPrompt: z.string(),
  sourceText: z.string().optional().describe('A passage of prose to analyze for style. Used by the extract-styleguide flow to feed the imported book text.'),
  genreTemplates: z.array(TemplateInfluenceProseSchema.nullable()).optional().describe('An array of prose objects for each selected genre influence.'),
  writerTemplates: z.array(TemplateInfluenceProseSchema.nullable()).optional().describe('An array of prose objects for each selected writer influence.'),
  universeTemplates: z.array(TemplateInfluenceProseSchema.nullable()).optional().describe('An array of prose objects for each selected universe influence.'),
}).merge(AGENT_SCHEMA_OBJECTS.sliderDefinitions.schema);

export type Input = z.infer<typeof InputSchema>;

/**
 * Hook Input type - accepts template IDs that the hook converts to template prose.
 *
 * Note: This is distinct from Input because the hook performs pre-processing
 * to load templates from IDs before calling executeAgent. The hook passes
 * the loaded templates to the agent, not the IDs.
 */
export type HookInput = {
  sourceText?: string;
  genreIds?: string[];
  writerIds?: string[];
  universeIds?: string[];
};

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output Schema - the analyzed slider values and extracted lexicon.
 */
export const OutputSchema = z.object({
  // Content guardrails (optional - may be present in source material)
  sexualContent: z.number().min(0).max(4).optional(),
  physicalViolence: z.number().min(0).max(4).optional(),
  emotionalViolence: z.number().min(0).max(4).optional(),
  profanity: z.number().min(0).max(4).optional(),

  // Style dimensions (required)
  narrativeDistance: z.number().min(0).max(4),
  visualSpecificity: z.number().min(0).max(4),
  syntacticComplexity: z.number().min(0).max(4),
  vocabularyRegister: z.number().min(0).max(4),
  descriptionDensity: z.number().min(0).max(4),
  pacingCadence: z.number().min(0).max(4),
  expositionIntegration: z.number().min(0).max(4),
  dialogueTexture: z.number().min(0).max(4),
  emotionalSaturation: z.number().min(0).max(4),
  atmosphericWeight: z.number().min(0).max(4),

  // Analysis metadata
  explanation: z.string().describe('Explanation of the analysis and a summary of the most important slider settings.'),
  toneKeywords: z.array(z.string()).optional().describe('A list of 3-5 keywords that capture the primary tone.'),
  recommendedPhrases: z.array(z.string()).optional().describe('A list of 5-10 words or short phrases that are characteristic of the analyzed style.'),
});

export type Output = z.infer<typeof OutputSchema>;
